let ctx = null;
let master = null;
let drone = null;
let enabled = false;
let noiseBuffer = null;

// E phrygian-dominant — the "ancient" scale: one voice per record
const NOTES = [329.63, 415.3, 493.88]; // E4, G#4, B4

const ensureCtx = () => {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    // noise bed — shared by the sistrum and the candle
    const len = ctx.sampleRate * 2;
    noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }
  return ctx;
};

const buildDrone = (c) => {
  // an original ambient piece — a drowned ancient world:
  // slow pad chords drifting like tides, sparse bell motifs, wave-breathing filter
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 620;
  lp.connect(master);

  // the sea breathing over the ruins
  const wave = c.createOscillator();
  wave.frequency.value = 0.035;
  const waveGain = c.createGain();
  waveGain.gain.value = 260;
  wave.connect(waveGain);
  waveGain.connect(lp.frequency);
  wave.start();

  // tonal centre — mystical D minor colours drifting to Bb, G, A
  const CHORDS = [
    [146.83, 220.0, 349.23, 329.63],
    [116.54, 174.61, 293.66, 261.63],
    [98.0, 146.83, 246.94, 220.0],
    [110.0, 164.81, 277.18, 246.94],
  ];
  const BELLS = [587.33, 659.25, 698.46, 783.99, 880.0, 1046.5];

  const state = { voices: [], chord: 0, padTimer: null, bellTimer: null, wave };

  const playChord = (freqs) =>
    freqs.map((f, i) => {
      const o = c.createOscillator();
      o.type = i < 2 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = i % 2 ? 5 : -5;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, c.currentTime);
      g.gain.linearRampToValueAtTime(i < 2 ? 0.055 : 0.024, c.currentTime + 4.5);
      o.connect(g);
      g.connect(lp);
      o.start();
      return { o, g };
    });

  const ringBell = () => {
    const f = BELLS[Math.floor(Math.random() * BELLS.length)];
    const t = c.currentTime;
    [
      { ff: f, v: 0.03, d: 3.6 },
      { ff: f * 2.01, v: 0.012, d: 2.4 },
    ].forEach(({ ff, v, d }) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = ff;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(v, t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      o.connect(g);
      g.connect(master);
      o.start(t);
      o.stop(t + d + 0.1);
    });
  };

  state.voices = playChord(CHORDS[0]);

  state.padTimer = setInterval(() => {
    if (!enabled) return;
    // let the old chord sink
    state.voices.forEach(({ o, g }) => {
      g.gain.cancelScheduledValues(c.currentTime);
      g.gain.setValueAtTime(g.gain.value, c.currentTime);
      g.gain.linearRampToValueAtTime(0.0001, c.currentTime + 5);
      o.stop(c.currentTime + 5.2);
    });
    state.chord = (state.chord + 1) % CHORDS.length;
    state.voices = playChord(CHORDS[state.chord]);
  }, 15000);

  state.bellTimer = setInterval(() => {
    if (enabled && Math.random() < 0.75) ringBell();
  }, 7000);

  return state;
};

export const soundscapeOn = () => {
  const c = ensureCtx();
  c.resume();
  enabled = true;
  if (!drone) drone = buildDrone(c);
  master.gain.cancelScheduledValues(c.currentTime);
  master.gain.setValueAtTime(master.gain.value, c.currentTime);
  master.gain.linearRampToValueAtTime(0.15, c.currentTime + 3);
};

export const soundscapeOff = () => {
  if (!ctx) return;
  enabled = false;
  master.gain.cancelScheduledValues(ctx.currentTime);
  master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
  master.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.6);
};

export const chime = (index) => {
  if (!ctx || !enabled) return;
  const c = ctx;
  const f = NOTES[index % NOTES.length];
  const t = c.currentTime;

  // temple bell / struck bowl — slightly inharmonic partials, long decay
  [
    { ff: f, v: 0.15, d: 2.4, type: "sine" },
    { ff: f * 2.76, v: 0.05, d: 1.5, type: "sine" },
    { ff: f * 5.4, v: 0.02, d: 0.9, type: "sine" },
    { ff: f * 0.5, v: 0.06, d: 2.0, type: "triangle" },
  ].forEach(({ ff, v, d, type }) => {
    const o = c.createOscillator();
    o.type = type;
    o.frequency.value = ff;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(v, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g);
    g.connect(master);
    o.start(t);
    o.stop(t + d + 0.1);
  });

  // sistrum shimmer — the sacred rattle
  const src = c.createBufferSource();
  src.buffer = noiseBuffer;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 6800;
  bp.Q.value = 1.2;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.035, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
  src.connect(bp);
  bp.connect(g);
  g.connect(master);
  src.start(t);
  src.stop(t + 0.3);
};

/* a moth fluttering past — soft rapid wingbeats wandering through the dark */
export const mothFlutter = () => {
  if (!ctx || !enabled) return;
  const c = ctx;
  const t = c.currentTime;

  const src = c.createBufferSource();
  src.buffer = noiseBuffer;
  src.loop = true;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(420, t);
  // the moth wanders — direction shifts
  for (let i = 0; i < 6; i++) {
    bp.frequency.setTargetAtTime(320 + Math.random() * 380, t + i * 0.12, 0.05);
  }

  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.06, t + 0.08);
  g.gain.setTargetAtTime(0.0001, t + 0.75, 0.12);

  // wingbeat tremolo
  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 24;
  const lg = c.createGain();
  lg.gain.value = 0.035;
  lfo.connect(lg);
  lg.connect(g.gain);

  src.connect(bp);
  bp.connect(g);
  g.connect(master);
  src.start(t);
  lfo.start(t);
  src.stop(t + 1);
  lfo.stop(t + 1);
};

/* candlelight flicker — flame hiss, flutter, and tiny wick pops */
export const candleFlicker = () => {
  if (!ctx || !enabled) return;
  const c = ctx;
  const t = c.currentTime;

  // the flame's soft breathing hiss
  const src = c.createBufferSource();
  src.buffer = noiseBuffer;
  src.loop = true;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 620;
  bp.Q.value = 0.8;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.05, t + 0.09);
  for (let i = 0; i < 10; i++) {
    g.gain.setTargetAtTime(0.018 + Math.random() * 0.05, t + 0.12 + i * 0.12, 0.05);
  }
  g.gain.setTargetAtTime(0.0001, t + 1.25, 0.16);
  src.connect(bp);
  bp.connect(g);
  g.connect(master);
  src.start(t);
  src.stop(t + 1.6);

  // wick pops
  for (let i = 0; i < 5; i++) {
    const pt = t + 0.08 + Math.random() * 1.1;
    const pop = c.createBufferSource();
    pop.buffer = noiseBuffer;
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1800 + Math.random() * 1600;
    const pg = c.createGain();
    pg.gain.setValueAtTime(0.0001, pt);
    pg.gain.exponentialRampToValueAtTime(0.035 + Math.random() * 0.05, pt + 0.008);
    pg.gain.exponentialRampToValueAtTime(0.0001, pt + 0.05);
    pop.connect(hp);
    hp.connect(pg);
    pg.connect(master);
    pop.start(pt);
    pop.stop(pt + 0.08);
  }
};
